import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  TuiAppearance,
  TuiButton,
  TuiError,
  TuiLabel,
  TuiAlertService,
  TuiTextfieldComponent,
  TuiNotification,
  TuiTextfieldDirective,
} from '@taiga-ui/core';
import { TuiCardLarge } from '@taiga-ui/layout';
import { AsyncPipe, NgIf } from '@angular/common';
import { TuiFieldErrorPipe, tuiValidationErrorsProvider } from '@taiga-ui/kit';
import { tuiMarkControlAsTouchedAndValidate } from '@taiga-ui/cdk';
import { TuiInputModule } from '@taiga-ui/legacy';
import { UserService } from '../services/user.service';
import { User } from '../models/user';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CommonService } from '../services/common.service';
import {
  SignaturePad,
  SignaturePadModule,
} from '@viablelogic/ngx-signature-pad';

@Component({
  selector: 'app-profil',
  standalone: true,
  imports: [
    HeaderComponent,
    ReactiveFormsModule,
    SignaturePadModule,
    TuiAppearance,
    TuiCardLarge,
    TuiLabel,
    TuiTextfieldComponent,
    TuiTextfieldDirective,
    TuiInputModule,
    TuiButton,
    AsyncPipe,
    TuiError,
    TuiFieldErrorPipe,

    NgIf,
    TuiNotification,
  ],
  templateUrl: './profil.component.html',
  styleUrl: './profil.component.scss',
  providers: [
    tuiValidationErrorsProvider({
      required: 'Champ obligatoire',
      maxlength: ({ requiredLength }: { requiredLength: string }) =>
        `Longueur maximale — ${requiredLength}`,
      minlength: ({ requiredLength }: { requiredLength: string }) =>
        `Longueur minimale — ${requiredLength}`,
    }),
  ],
})
export class ProfilComponent implements OnInit {
  private route = inject(ActivatedRoute);
  errorMessage: string | null = null;
  private userService = inject(UserService);
  readonly authService = inject(AuthService);
  readonly common = inject(CommonService);
  private alerts = inject(TuiAlertService);
  user0: WritableSignal<User | undefined> = signal(undefined);

  formMotDePasse!: FormGroup;

  formProfil!: FormGroup;

  @ViewChild(SignaturePad) signaturePad: SignaturePad | undefined;

  signaturePadOptions: Object = {
    // passed through to szimek/signature_pad constructor
    minWidth: 1,
    canvasWidth: 500,
    canvasHeight: 200,
  };

  passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('new_password')?.value;
    const confirmPassword = control.get('confirm_password')?.value;

    return newPassword && confirmPassword && newPassword !== confirmPassword
      ? { passwordsMismatch: true }
      : null;
  }

  onSubmitMotDePasse() {
    if (this.user0()) {
      if (this.formMotDePasse.valid) {
        this.errorMessage = null;
        this.userService
          .updatePwd(this.user0()!.id, this.formMotDePasse.value)
          .subscribe({
            next: (response) => {
              this.common.showSuccessAlert('Mot de passe modifié avec succès.');
            },
            error: (err) => {
              this.errorMessage =
                err.error?.message ||
                'Une erreur est survenue. Veuillez réessayer.';
            },
          });
      } else {
        tuiMarkControlAsTouchedAndValidate(this.formMotDePasse);
      }
    }
  }

  onSubmitProfil() {
    if (this.user0()) {
      this.user0()!.signature = this.formProfil.value.signature;
      this.userService.push(this.user0()!).subscribe({
        next: (response) => {
          this.common.showSuccessAlert('Profil modifié avec succès.');
        },
        error: (err) => {
          this.common.showErrorAlert(
            'Une erreur est survenue. Veuillez réessayer.',
          );
        },
      });
    }
  }

  showSuccessAlert(message: string) {
    const alertSubscription = this.alerts
      .open(message, {
        label: 'Succès',
        appearance: 'success',
        autoClose: 10000,
      })
      .subscribe();
  }

  ngOnInit(): void {
    this.formProfil = new FormGroup({
      login: new FormControl({ value: '', disabled: true }, [
        Validators.required,
      ]),
      signature: new FormControl(['', [Validators.required]]),
    });

    this.formMotDePasse = new FormGroup(
      {
        password: new FormControl('', []),
        new_password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirm_password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
      },
      { validators: this.passwordsMatchValidator },
    );

    this.route.params.subscribe((params) => {
      this.userService.pullDetail(params['id']).subscribe({
        next: (user: User) => {
          this.user0.set(user);
          this.formProfil.controls['login'].setValue(user.username);
          this.formProfil.controls['login'].disable();
          this.formProfil.controls['signature'].setValue(user.signature);
          this.signaturePad?.fromDataURL(user.signature);

          if (this.authService.user?.id == this.user0()?.id) {
            this.formMotDePasse.controls['password'].setValidators([
              Validators.required,
            ]);
          }
        },
      });
    });
  }

  ngAfterViewInit() {
    // this.signaturePad is now available
    this.signaturePad?.set('minWidth', 5); // set szimek/signature_pad options at runtime
    if (this.user0()) this.signaturePad?.fromDataURL(this.user0()!.signature);

    // this.signaturePad?.clear(); // invoke functions from szimek/signature_pad API
  }

  clearDraw() {
    this.signaturePad?.clear();
  }

  drawComplete() {
    // will be notified of szimek/signature_pad's onEnd event
    this.formProfil.patchValue({
      signature: this.signaturePad?.toDataURL(),
    });
  }
}
